import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import Header from './components/header';
import IframeViewer from './components/iframe-viewer';
import PdfViewer from './components/pdf-viewer';
import Notice from './components/notice';
import { getQuoteShareMetadataQuery } from './services/get-metadata-query';
import { useTimeSpentTracker } from './hooks/use-time-spent-tracker';
import { useStyles } from './styles';
import faviconDefault from '../../assets/icons/favicon.ico';
import faviconSinger from '../../assets/icons/singer.ico';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const SINGER_COMPANY_IDS = new Set(['3246', '3177']);

const QuoteSharePage = () => {
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { companyId, token } = useParams<{
    companyId: string;
    token: string;
  }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get('preview') === 'true';

  const enabled = Boolean(companyId && token);

  useEffect(() => {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;
    link.href =
      companyId && SINGER_COMPANY_IDS.has(companyId)
        ? faviconSinger
        : faviconDefault;
  }, [companyId]);

  const {
    data: metadata,
    isLoading: isMetadataLoading,
    isError: isMetadataError
  } = useQuery({
    ...getQuoteShareMetadataQuery(
      API_BASE_URL,
      companyId || '',
      token || '',
      isPreview
    ),
    enabled
  });

  const canViewHtml =
    metadata?.status === 'active' || metadata?.status === 'signed';

  useTimeSpentTracker({
    apiBaseUrl: API_BASE_URL,
    companyId: companyId || '',
    token: token || '',
    enabled: enabled && canViewHtml && !isPreview
  });

  useEffect(() => {
    document.title = metadata?.quoteName
      ? `Quote · ${metadata.quoteName}`
      : 'Quote';
  }, [metadata?.quoteName]);

  const shareBaseUrl = `${API_BASE_URL}/public/quote-shares/${companyId}/${token}`;
  const previewQuery = isPreview ? '?preview=true' : '';
  const htmlUrl = `${shareBaseUrl}/html${previewQuery}`;
  const pdfUrl = `${shareBaseUrl}/pdf${previewQuery}`;

  const [isDownloading, setIsDownloading] = useState(false);

  const downloadEnabled = canViewHtml && !isDownloading;

  const handleDownload = async () => {
    if (!canViewHtml || isDownloading) return;
    setIsDownloading(true);
    try {
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error(`status_${res.status}`);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const safeName = (metadata?.quoteName || 'quote')
        .replace(/[\\/:*?"<>|]+/g, '')
        .trim();
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `${safeName || 'quote'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } finally {
      setIsDownloading(false);
    }
  };

  const renderContent = () => {
    if (!enabled) return <Notice variant="notFound" />;

    if (isMetadataLoading) {
      return (
        <Box className={styles.loaderContainer}>
          <CircularProgress />
        </Box>
      );
    }

    if (isMetadataError || !metadata) return <Notice variant="notFound" />;

    if (canViewHtml)
      return isMobile ? (
        <PdfViewer src={pdfUrl} />
      ) : (
        <IframeViewer src={htmlUrl} />
      );

    if (metadata.status === 'superseded')
      return <Notice variant="updated" contact={metadata.contact} />;

    if (metadata.status === 'revoked')
      return <Notice variant="unavailable" contact={metadata.contact} />;

    return <Notice variant="notFound" />;
  };

  return (
    <Box className={styles.page}>
      <Header
        onDownload={handleDownload}
        downloadEnabled={downloadEnabled}
        isDownloading={isDownloading}
        isLogoLoading={enabled && isMetadataLoading}
        logoUrl={metadata?.branding?.companyLogo}
        companyName={metadata?.branding?.companyName}
      />
      <Box className={styles.content}>
        <Box className={styles.documentCard}>{renderContent()}</Box>
      </Box>
    </Box>
  );
};

export default QuoteSharePage;
